import * as XLSX from "xlsx";
import {
  systemTotals,
  centralHubLines,
  agencyBudgetLines,
  portfolioContracts,
  developerGroupTeam,
  developerGroupContract,
  developerGroupTotals,
  fiveYearForecast,
  fiveYearGrandTotal,
  fiveYearAllInTotal,
  operationalPhases,
  programGoalsForecast,
  programGoalsTotals,
  programOutcomeBenchmarks,
  triPortfolioROI,
  referralPartners,
} from "./proposalData";

// ---------- helpers ----------
const fmtCurrency = (n) => (typeof n === "number" ? n : 0);

function buildSheet(rows, opts = {}) {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  if (opts.colWidths) ws["!cols"] = opts.colWidths.map((w) => ({ wch: w }));
  if (opts.merges) ws["!merges"] = opts.merges;
  return ws;
}

// ---------- 1. Executive Summary ----------
function execSummarySheet() {
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const rows = [
    ["BAYTOWN BRIDGE · SERVICE CONNECTOR INITIATIVE"],
    ["5-Year Financial Projections & Program Goals Forecast"],
    [`Generated: ${today}`],
    [],
    ["EXECUTIVE SUMMARY"],
    [],
    ["Metric", "Year 1", "5-Year Total"],
    ["Households Served (Annual)", systemTotals.householdsServed, systemTotals.householdsServed * 5],
    ["Households Served — Cumulative Service-Years", "", programGoalsTotals.householdYears],
    ["Cost per Household (Recurring)", systemTotals.costPerHousehold, "—"],
    ["Cost per Household (All-In, incl. Dev Group)", systemTotals.costPerHouseholdAllIn, "—"],
    ["Service-Connector Caseload Cap", `1 : ${systemTotals.caseload}`, "—"],
    [],
    ["BUDGET HEADLINES (Year 1)"],
    ["Central Coordination Hub (City of Baytown)", fmtCurrency(systemTotals.centralHub)],
    ["Per Agency / Spoke", fmtCurrency(systemTotals.perAgency)],
    [`× ${systemTotals.agencyCount} Agencies`, fmtCurrency(systemTotals.totalAgencies)],
    ["Recurring Subtotal (Hub + 3 Agencies)", fmtCurrency(systemTotals.grandTotal)],
    ["Sterile Mate Developer Group · Year 1", fmtCurrency(systemTotals.developerGroupYr1)],
    ["TOTAL YEAR 1 INVESTMENT (All-In)", fmtCurrency(systemTotals.grandTotalAllIn)],
    [],
    ["5-YEAR TOTALS"],
    ["Recurring System (Hub + 3 Agencies)", fmtCurrency(fiveYearGrandTotal)],
    ["Developer Group (2-Yr Firm + 3-Yr Options)", fmtCurrency(developerGroupTotals.fiveYearAllOptions)],
    ["GRAND TOTAL · 5-YEAR ALL-IN", fmtCurrency(fiveYearAllInTotal)],
    [],
    ["NOTES"],
    ["3% annual cost-of-living raise on all salaried positions."],
    ["Non-salary operating costs (software, evaluation, admin, office ops) held flat."],
    ["Developer Group declines year-over-year as Baytown achieves operational independence."],
    ["Years 3–5 of Developer Group are City Options (not firm commitments)."],
  ];
  return buildSheet(rows, { colWidths: [50, 18, 18] });
}

// ---------- 2. 5-Year Forecast ----------
function fiveYearForecastSheet() {
  const header = [
    "Year",
    "Central Hub",
    "Per Agency",
    "Total 3 Agencies",
    "Recurring Subtotal",
    "Developer Group",
    "Total All-In",
    "$ / HH (Recurring)",
    "$ / HH (All-In)",
  ];
  const rows = [
    ["BAYTOWN BRIDGE · 5-YEAR FINANCIAL FORECAST"],
    ["Households Served per Year: 1,500 · Caseload Cap: 1:38 · 3% annual COLA"],
    [],
    header,
    ...fiveYearForecast.map((y) => [
      y.year,
      y.hub,
      y.perAgency,
      y.totalAgencies,
      y.system,
      y.devGroup,
      y.systemAllIn,
      y.costPerHH,
      y.costPerHHAllIn,
    ]),
    [],
    [
      "5-YEAR TOTAL",
      fiveYearForecast.reduce((s, y) => s + y.hub, 0),
      "—",
      fiveYearForecast.reduce((s, y) => s + y.totalAgencies, 0),
      fiveYearGrandTotal,
      developerGroupTotals.fiveYearAllOptions,
      fiveYearAllInTotal,
      "—",
      "—",
    ],
  ];
  return buildSheet(rows, { colWidths: [14, 14, 14, 18, 18, 16, 16, 18, 18] });
}

// ---------- 3. Year 1 · Central Hub Budget ----------
function hubBudgetSheet() {
  const rows = [
    ["PART A · CENTRAL COORDINATION HUB (CITY OF BAYTOWN)"],
    ["Year 1 Detailed Line Items · Permanent City of Baytown infrastructure"],
    [],
    ["Line Item", "Detail", "Year 1 Cost"],
    ...centralHubLines.map((l) => [l.line, l.detail, l.cost]),
    [],
    ["HUB TOTAL · YEAR 1", "", systemTotals.centralHub],
  ];
  return buildSheet(rows, { colWidths: [42, 80, 16] });
}

// ---------- 4. Year 1 · Per-Agency Budget ----------
function agencyBudgetSheet() {
  const rows = [
    ["PART B · PER-AGENCY OPERATING BUDGET (× 3 SPOKES)"],
    ["Each agency manages 500 households / year · Caseload 1:38"],
    [],
    ["Role", "FTE", "Detail", "Year 1 Cost"],
    ...agencyBudgetLines.map((l) => [l.role, l.fte, l.detail, l.cost]),
    [],
    ["PER-AGENCY TOTAL", "", "", systemTotals.perAgency],
    [`× ${systemTotals.agencyCount} AGENCIES`, "", "", systemTotals.totalAgencies],
  ];
  return buildSheet(rows, { colWidths: [38, 8, 70, 16] });
}

// ---------- 5. Developer Group · Sterile Mate LLC ----------
function developerGroupSheet() {
  const rows = [
    ["SECTION C · STERILE MATE DEVELOPER GROUP"],
    ["2-year firm engagement + 3-year City options · Builds & hands off to Baytown"],
    [],
    ["TEAM ROSTER · YEAR 1"],
    ["Role", "Who", "Detail", "Year 1 Cost"],
    ...developerGroupTeam.map((m) => [m.role, m.who, m.detail, m.cost]),
    ["", "", "Team Salaries Subtotal (Yr 1)", developerGroupTotals.teamSalariesYr1],
    ["", "", "Direct Expenses (curriculum, materials, travel)", developerGroupTotals.directExpensesYr1],
    ["", "", "YEAR 1 DEVELOPER GROUP TOTAL", developerGroupTotals.year1],
    [],
    ["MULTI-YEAR CONTRACT SCHEDULE"],
    ["Period", "Phase", "Detail", "Cost"],
    ...developerGroupContract.map((c) => [c.period, c.phase, c.detail, c.cost]),
    [],
    ["2-YEAR FIRM COMMITMENT", "", "", developerGroupTotals.twoYearFirm],
    ["5-YEAR (if all options exercised)", "", "", developerGroupTotals.fiveYearAllOptions],
  ];
  return buildSheet(rows, { colWidths: [32, 36, 70, 16] });
}

// ---------- 6. Tri-Portfolio Contracts ----------
function portfolioSheet() {
  const rows = [
    ["TRI-PORTFOLIO CONTRACT STRUCTURE"],
    ["Three competitively-selected nonprofit Spokes · 5-yr contract, annual review"],
    [],
    ["#", "Portfolio", "Households", "Annual Contract", "Focus"],
    ...portfolioContracts.map((p) => [p.id, p.name, p.households, p.cost, p.focus]),
    [],
    ["TOTAL", "3 Spokes", systemTotals.householdsServed, systemTotals.totalAgencies, ""],
  ];
  return buildSheet(rows, { colWidths: [4, 38, 12, 16, 70] });
}

// ---------- 7. Program Goals Forecast ----------
function programGoalsSheet() {
  const header = [
    "Year",
    "Households",
    "Seniors",
    "Families",
    "Lee College Enrolled",
    "Lee College Graduates",
    "Home Mods Completed",
    "Medicare Enrollments",
    "Benefits Recovered ($)",
    "Evictions Prevented",
    "Notes",
  ];
  const rows = [
    ["PROGRAM GOALS · 5-YEAR OUTCOME FORECAST"],
    ["Forward-looking program-side commitments paired with budget commitments"],
    [],
    header,
    ...programGoalsForecast.map((y) => [
      y.year,
      y.households,
      y.seniors,
      y.families,
      y.leeCollegeEnrollment,
      y.leeCollegeGraduates,
      y.homeMods,
      y.medicareEnrollment,
      y.benefitsRecovered,
      y.evictionsPrevented,
      y.note,
    ]),
    [],
    [
      "5-YR TOTAL",
      "—",
      "—",
      "—",
      programGoalsTotals.leeCollegeEnrollmentTotal,
      programGoalsTotals.leeCollegeGraduatesTotal,
      programGoalsTotals.homeModsTotal,
      programGoalsTotals.medicareEnrollmentsTotal,
      programGoalsTotals.benefitsRecoveredTotal,
      programGoalsTotals.evictionsPreventedTotal,
      `${programGoalsTotals.householdYears.toLocaleString()} cumulative household service-years`,
    ],
  ];
  return buildSheet(rows, { colWidths: [12, 12, 10, 10, 16, 16, 14, 16, 18, 14, 80] });
}

// ---------- 8. Outcome Benchmarks ----------
function benchmarksSheet() {
  const rows = [
    ["PROGRAM OUTCOME BENCHMARKS"],
    ["Non-negotiable performance standards · enforced annually per Spoke"],
    [],
    ["Metric", "Target", "Rationale"],
    ...programOutcomeBenchmarks.map((b) => [b.metric, b.target, b.rationale]),
  ];
  return buildSheet(rows, { colWidths: [40, 22, 70] });
}

// ---------- 9. Operational Phases / Launch Plan ----------
function launchPlanSheet() {
  const rows = [
    ["YEAR-1 OPERATIONAL LAUNCH PLAN"],
    ["From Council resolution to 1,500 active households in six months"],
    [],
    ["Period", "Milestone", "Description"],
    ...operationalPhases.map((p) => [p.period, p.title, p.body]),
  ];
  return buildSheet(rows, { colWidths: [16, 50, 90] });
}

// ---------- 10. ROI / Cost Avoidance ----------
function roiSheet() {
  const rows = [
    ["ECONOMIC ROI · COMPARED TO WHAT?"],
    [],
    ["Item", "Cost", "Note"],
    ...triPortfolioROI.map((r) => [r.label, r.value, r.note]),
  ];
  return buildSheet(rows, { colWidths: [40, 22, 80] });
}

// ---------- 11. Referral Partners ----------
function partnersSheet() {
  const rows = [
    ["REFERRAL PARTNER NETWORK"],
    [],
    ["Partner Organization", "Role", "Track"],
    ...referralPartners.map((p) => [p.name, p.role, p.track]),
  ];
  return buildSheet(rows, { colWidths: [34, 70, 14] });
}

// ---------- Master export ----------
export function generateBudgetSpreadsheet() {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, execSummarySheet(), "Executive Summary");
  XLSX.utils.book_append_sheet(wb, fiveYearForecastSheet(), "5-Year Forecast");
  XLSX.utils.book_append_sheet(wb, hubBudgetSheet(), "Yr1 · Hub Budget");
  XLSX.utils.book_append_sheet(wb, agencyBudgetSheet(), "Yr1 · Per-Agency Budget");
  XLSX.utils.book_append_sheet(wb, developerGroupSheet(), "Developer Group");
  XLSX.utils.book_append_sheet(wb, portfolioSheet(), "Tri-Portfolio Contracts");
  XLSX.utils.book_append_sheet(wb, programGoalsSheet(), "Program Goals Forecast");
  XLSX.utils.book_append_sheet(wb, benchmarksSheet(), "Outcome Benchmarks");
  XLSX.utils.book_append_sheet(wb, launchPlanSheet(), "Launch Plan");
  XLSX.utils.book_append_sheet(wb, roiSheet(), "ROI · Cost Avoidance");
  XLSX.utils.book_append_sheet(wb, partnersSheet(), "Referral Partners");

  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `Baytown-Bridge-5Yr-Financials-${stamp}.xlsx`);
}
